import * as React from 'react'
import { Button, createForm } from '@lacedltd/laced-components'
import { usePrevious } from 'react-use'
import { useLocale, useCountryList, useTranslation } from 'hooks'
import { LacedAvailableLocations } from 'types/localisation'
import * as S from '../../SiteSettings.styles'
import { SiteSettingsUpdate } from '../../types'
import {
  generateCurrency,
  generateLocations,
  generateLocales
} from './generateSelectFields'
import {
  siteSettingsSchema,
  SiteSettingsSchemaProps
} from './SiteSettingsForm.schema'

const Form = createForm(siteSettingsSchema)

const SiteSettingsForm = ({
  dialogControls: { open, setOpen },
  updateSiteSettings,
  isLoading
}: SiteSettingsFormProps) => {
  const { t } = useTranslation()
  const { countries } = useCountryList({ query: 'Supported' })
  const { location, locale, currency, localeObject, currencyObject } =
    useLocale()

  const [isSubmit, setIsSubmitting] = React.useState(false)
  const prevOpenState = usePrevious(open)

  const methods = Form.useForm({
    defaultValues: {
      location: location.code,
      locale,
      currency
    }
  })

  const onSubmit = async (newSettings: SiteSettingsSchemaProps) => {
    if (
      newSettings.locale !== locale ||
      newSettings.currency !== currency ||
      newSettings.location !== location.code
    ) {
      setIsSubmitting(true)

      await updateSiteSettings(
        newSettings.locale,
        newSettings.currency,
        newSettings.location as LacedAvailableLocations
      )
    }

    setOpen(false)
  }

  React.useEffect(() => {
    // If the modal gets closed and its not via the onSubmit fn
    // we update the site settings to align with the current site
    // they are on.
    if (prevOpenState && !open && !isSubmit) {
      updateSiteSettings(locale, currency, location.code)
    }

    setIsSubmitting(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prevOpenState])

  return (
    <S.SiteSettingsForm>
      <Form.Container methods={methods} onSubmit={onSubmit}>
        <Form.SelectInput
          name="location"
          label={t('react_components.site_settings.shipping_destination')}
          values={generateLocations(countries, location.code)}
          loading={countries?.length === 0}
        />

        <Form.SelectInput
          name="currency"
          label={t('react_components.site_settings.currency')}
          values={generateCurrency(currencyObject)}
        />

        <Form.SelectInput
          name="locale"
          label={t('react_components.site_settings.language')}
          values={generateLocales(localeObject)}
        />

        <Button
          loading={isLoading}
          variant="primary"
          data-testid="submit-site-settings"
        >
          {t('shared.submit')}
        </Button>
      </Form.Container>
    </S.SiteSettingsForm>
  )
}

export default SiteSettingsForm

interface SiteSettingsFormProps {
  dialogControls: {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
  }
  updateSiteSettings: SiteSettingsUpdate
  isLoading: boolean
}
